/* eslint-disable react-refresh/only-export-components */ // FIXME
import { Alert, ConfigProvider, Flex, Skeleton, Table } from "antd"
// eslint-disable-next-line no-restricted-imports -- deprecated file
import { useQuery } from "@apollo/client"
import {
  formatDateForTable,
  formatLocalCalendarDateWithTime,
  formatLocalDateAsData,
} from "src/global/utils/formatTime.ts"
import {
  formatNumber,
  formatNumberAsPercentage,
  formatNumberScaleMaxDigits as formatNumberScaleMaxDecimalDigits,
} from "src/global/utils/formatNumber.tsx"
import { GenerateColumnDescriptiveMetricsButton } from "src/pages/DataAssets/views/AssetDetails/RunColumnDescriptiveMetricsButton.tsx"
import { useJobState } from "src/global/hooks/useJobState.ts"
import { useEffect, useState } from "react"
import { usePreviousValue } from "src/global/hooks/usePreviousValue.ts"
import { Metric, MetricRunAggregate, MetricType, MetricValueUnion } from "src/api/graphql/graphql.ts"
import { MetricsTableEmptyScreen } from "src/pages/DataAssets/views/AssetDetails/MetricsTableEmptyScreen.tsx"
import { AgentNotConnectedModal } from "src/pages/DataAssets/views/AssetDetails/AgentNotConnectedModal.tsx"
import { SkeletonTable, SkeletonTableColumnsType } from "src/global/components/SkeletonTable.tsx"
import { useTheme } from "styled-components"
import { ConditionalTooltip } from "src/global/ui/Tooltip/ConditionalTooltip.tsx"
import { BodyNormal, BodyStrong, DescriptionNormal } from "src/global/ui/typography/Text/Text.tsx"
import { useRequireRole } from "src/global/hooks/useRequireRole.ts"
import { Link } from "src/global/ui/typography/Link/Link.tsx"
import { graphql } from "src/api/graphql/gql"

export const METRIC_UNAVAILABLE = "-"

// TODO: Improve this. we need a more dynamic way of knowing the full list of metrics we can fetch.
// These is a hardcoded list of all the metrics we would fetch for the metrics table.
export const DEFAULT_METRICS = [
  "COLUMN_MEAN",
  "COLUMN_MEDIAN",
  "COLUMN_MIN",
  "COLUMN_MAX",
  "COLUMN_NULL_COUNT",
  "TABLE_COLUMNS",
  "TABLE_COLUMN_TYPES",
  "TABLE_ROW_COUNT",
] satisfies MetricType[]

interface MetricTabProps {
  assetId: string
}
interface MetricsTableDatum {
  key: number
  colName: string
  type: string
  rawType: string
  min: number | string // datetime values are returned as strings
  max: number | string // datetime values are returned as strings
  mean: number
  median: number
  nullPct: string
}

export function filterExtendedTypeInformation(type: string | null | undefined): string {
  // Filtering extended type information for Snowflake tables, which include extended information in parentheses (e.g. VARCHAR(16777216))
  if (type === null || type === undefined) {
    return ""
  }
  return type.split("(")[0]
}

function isQueryTypeDataAssetConfig(config?: string | null): boolean {
  if (!config) {
    return false
  }
  try {
    const parsed_config = JSON.parse(config)
    return parsed_config.type.toLowerCase() === "query"
  } catch {
    return false
  }
}

export function formatNumberScaleFromType(type: string | null | undefined, value: number): string {
  // Formatting number scale based on column type
  // Note this logic is snowflake specific
  if (type === null || type === undefined) {
    return ""
  }
  // FLOAT values (and synonyms FLOAT , FLOAT4 , FLOAT8, DOUBLE, DOUBLE PRECISION, REAL see https://docs.snowflake.com/en/sql-reference/data-types-numeric#float-float4-float8 and https://docs.snowflake.com/en/sql-reference/data-types-numeric#double-double-precision-real) don't have an associated scale and are formatted with a default maximum number of decimal places. The snowflake-sqlalchemy library used to compute the underlying metric converts synonyms to FLOAT or REAL, so we only need to check for those. See https://github.com/snowflakedb/snowflake-sqlalchemy/blob/v1.5.1/src/snowflake/sqlalchemy/snowdialect.py#L75-L111 for the latest version.
  if (type === "FLOAT" || type === "REAL") {
    return formatNumberScaleMaxDecimalDigits(value)
  }
  // Regex to extract scale (second num in parens) from numeric types E.g. the scale of NUMBER(38, 0) is 0
  const numericScaleRegex = ".*(\\d+, *(?<scale>\\d+)).*"
  const scale = Number(type.match(numericScaleRegex)?.groups?.scale)
  // NaN check is needed because scale is NaN for types without a scale (e.g. INT):
  if (typeof scale === "number" && !isNaN(scale)) {
    return formatNumberScaleMaxDecimalDigits(value, scale)
  }
  return formatNumberScaleMaxDecimalDigits(value)
}

export function calculateNullPercentage(nullCount: number | null | undefined, rowCount: number): string | null {
  if (nullCount === null || nullCount === undefined) {
    return null
  }
  return ((100.0 * Number(nullCount)) / rowCount).toFixed(2)
}

function convertMetricsDataToTableData(metricRun?: MetricRunAggregate | null): MetricsTableDatum[] | undefined {
  const rowCount = Number(metricRun?.rowCount ?? 0)
  return metricRun?.metrics.map(
    (metric, index) =>
      ({
        key: index,
        colName: metric.columnName,
        type: filterExtendedTypeInformation(metric.columnDataType),
        rawType: metric.columnDataType,
        min: getMetricValue(metric.valueRangeMin, metric.valueRangeMinUnion as MetricValueUnion),
        max: getMetricValue(metric.valueRangeMax, metric.valueRangeMaxUnion as MetricValueUnion),
        mean: metric.mean,
        median: metric.median,
        nullPct: calculateNullPercentage(metric.nullCount, rowCount),
      }) as MetricsTableDatum,
  )
}

type MixedValueType = number | string | null

export function compareMixedTypes(a: MixedValueType, b: MixedValueType) {
  // Two phases of sorting:
  //   By type, then by value
  //   Ex. datetimes are grouped then sorted together, and numbers are grouped and sorted
  //   order: null, number, string

  const typeA = typeof a
  const typeB = typeof b

  if (typeA === typeB) {
    switch (typeA) {
      case "string":
        return (a as string).localeCompare(b as string)
      case "number":
      default:
        return Number(a) - Number(b)
    }
  }

  // null is special bc we want to be lower than numbers
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === null || a === undefined ? -1 : 1
  }

  // basic string sort of types
  return typeA > typeB ? 1 : -1
}

function getMetricValue(metricValue: number | null | undefined, metricValueUnion: MetricValueUnion): MixedValueType {
  // use Union type to get numeric or string (datetime) value
  return metricValueUnion?.__typename === "MetricValueFloatType"
    ? (metricValueUnion.floatValue ?? null)
    : (metricValueUnion?.stringValue ?? null)
}

function renderIfSupported(value: MixedValueType, record: MetricsTableDatum) {
  // https://docs.snowflake.com/en/sql-reference/data-types-datetime
  // DATE, TIMESTAMP, TIMESTAMP_NTZ, ...
  const isTimeBasedTypeRegex = "(TIME|DATE)"
  if (record.type.match(isTimeBasedTypeRegex) || value === null || value === undefined) {
    return METRIC_UNAVAILABLE
  }
  return formatNumberScaleFromType(record.rawType, value as number)
}

function isDate(value: string) {
  const parsed = Date.parse(value)
  // #parse returns NaN when invalid date
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse#return_value
  if (isNaN(parsed)) {
    return false
  }
  return true
}

export const MetricValueStringTypeFragment = graphql(`
  fragment MetricValueStringTypeFragment on MetricValueStringType {
    __typename
    stringValue
  }
`)

export const MetricValueFloatTypeFragment = graphql(`
  fragment MetricValueFloatTypeFragment on MetricValueFloatType {
    __typename
    floatValue
  }
`)

export const MetricRunAggregateDocument = graphql(`
  query MetricRunAggregate($id: UUID!, $metricNames: [MetricType!]!) {
    dataAsset(id: $id) {
      id
      name
      datasourceV2 {
        id
        name
        type
      }
      config
      createdAt
      metricRunAggregate(metricNames: $metricNames) {
        lastFetched
        rowCount
        metrics {
          columnName
          columnDataType
          valueRangeMin
          valueRangeMax
          valueRangeMinUnion {
            __typename
            ...MetricValueStringTypeFragment
            ...MetricValueFloatTypeFragment
          }
          valueRangeMaxUnion {
            __typename
            ...MetricValueStringTypeFragment
            ...MetricValueFloatTypeFragment
          }
          mean
          median
          nullCount
        }
      }
    }
  }
`)

export function MetricsTable({ assetId }: MetricTabProps) {
  const jobState = useJobState({
    dataAssetId: assetId,
    type: "GenerateColumnDescriptiveMetrics",
  })
  const [isAgentErrorModalVisible, setIsAgentErrorModalVisible] = useState(false)
  // metrics query
  const {
    data: metricRunAggregateData,
    loading: metricRunAggregateLoading,
    refetch: refetchMetricRunAggregate,
  } = useQuery(MetricRunAggregateDocument, {
    variables: {
      id: assetId,
      metricNames: DEFAULT_METRICS,
    },
    notifyOnNetworkStatusChange: true,
  })
  const loading = metricRunAggregateLoading
  const previousJobStatus = usePreviousValue(jobState?.status)

  function formatDateOrNumberValue(value: string | number, record: MetricsTableDatum) {
    if (value === null || value === undefined) {
      return METRIC_UNAVAILABLE
    }
    if (typeof value === "string") {
      if (!isDate(value)) {
        return METRIC_UNAVAILABLE
      }
      return formatDateForTable(value)
    }
    return formatNumberScaleFromType(record.rawType, value)
  }

  useEffect(() => {
    if (jobState?.status === "complete" && ["inProgress", undefined].includes(previousJobStatus)) {
      refetchMetricRunAggregate()
    }
  }, [jobState?.status, previousJobStatus, refetchMetricRunAggregate])

  const theme = useTheme()
  const dataSource = convertMetricsDataToTableData(metricRunAggregateData?.dataAsset?.metricRunAggregate)
  const numberColWidth = "120px"

  const isQueryTypeDataAsset = isQueryTypeDataAssetConfig(metricRunAggregateData?.dataAsset?.config)

  const columns = [
    {
      title: "Column",
      dataIndex: "colName",
      key: "colName",
      width: "200px",
      defaultSortOrder: "ascend" as const,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => a.colName.localeCompare(b.colName),
      render: (value: string) => <BodyNormal>{value}</BodyNormal>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "150px",
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => a.type.localeCompare(b.type),
      render: (value: string) => <BodyNormal>{value}</BodyNormal>,
    },
    {
      title: "Min",
      dataIndex: "min",
      key: "min",
      width: numberColWidth,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => compareMixedTypes(a.min, b.min),
      render: (value: string, record: MetricsTableDatum) => (
        <BodyNormal>{formatDateOrNumberValue(value, record)}</BodyNormal>
      ),
    },
    {
      title: "Max",
      dataIndex: "max",
      key: "max",
      width: numberColWidth,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => compareMixedTypes(a.max, b.max),
      render: (value: string, record: MetricsTableDatum) => (
        <BodyNormal>{formatDateOrNumberValue(value, record)}</BodyNormal>
      ),
    },
    {
      title: "Mean",
      dataIndex: "mean",
      key: "mean",
      width: numberColWidth,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => compareMixedTypes(a.mean, b.mean),
      render: (value: string, record: MetricsTableDatum) => <BodyNormal>{renderIfSupported(value, record)}</BodyNormal>,
    },
    {
      title: "Median",
      dataIndex: "median",
      key: "median",
      width: numberColWidth,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => compareMixedTypes(a.median, b.median),
      render: (value: string, record: MetricsTableDatum) => <BodyNormal>{renderIfSupported(value, record)}</BodyNormal>,
    },
    {
      title: "Null %",
      dataIndex: "nullPct",
      key: "nullPct",
      width: numberColWidth,
      sorter: (a: MetricsTableDatum, b: MetricsTableDatum) => Number(a.nullPct) - Number(b.nullPct),
      render: (value: string) => <BodyNormal>{formatNumberAsPercentage(value)}</BodyNormal>,
    },
  ]

  const queryAssetWarningMessage = (
    <>
      Metrics for query assets are not supported. Instead,{" "}
      <Link to="https://docs.snowflake.com/en/sql-reference/sql/create-view" target="_blank">
        create a view
      </Link>{" "}
      in your database from your query and use that to create a table asset within GX.
    </>
  )

  const caption = (
    <div
      style={{
        backgroundColor: theme.colors.neutralColorPalette.whites.white,
        borderTopLeftRadius: theme.spacing.xxs,
        borderTopRightRadius: theme.spacing.xxs,
      }}
    >
      <Caption
        metricRunAggregate={metricRunAggregateData?.dataAsset?.metricRunAggregate}
        dataAssetId={assetId}
        loading={loading}
      />
    </div>
  )

  const metricsDisplay = dataSource ? (
    <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]} caption={caption}>
      {caption}
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBorderRadius: 0, // no table header border radius b/c caption row above table has top border radius of 8px
            },
          },
        }}
      >
        <Table dataSource={dataSource} columns={columns} pagination={false} scroll={{ x: "max-content", y: "100%" }} />
      </ConfigProvider>
    </SkeletonTable>
  ) : (
    <MetricsTableEmptyScreen dataAssetId={assetId} disabled={isQueryTypeDataAsset} />
  )

  return loading ? null : (
    <>
      {isQueryTypeDataAsset && <Alert message={queryAssetWarningMessage} type="warning" showIcon />}
      {metricsDisplay}

      <AgentNotConnectedModal isVisible={isAgentErrorModalVisible} setIsVisible={setIsAgentErrorModalVisible} />
    </>
  )
}

export function checkIfFullCdmHasBeenRun(metricRun?: MetricRunAggregate | null): boolean {
  // Check if the full CDMs have been run by checking if mean, median, valueRangeMin, and valueRangeMax are not null which are currently the metrics not ran on Asset creation.
  const deferredMetrics: (keyof Metric)[] = ["mean", "median", "valueRangeMin", "valueRangeMax"]

  let cdmHasRun = false
  for (const metricKey of deferredMetrics) {
    if (metricRun?.metrics.some((metric) => metric[metricKey] !== null && metric[metricKey] !== undefined)) {
      cdmHasRun = true
      break
    }
  }
  return cdmHasRun
}

export function Caption({
  metricRunAggregate,
  dataAssetId,
  loading,
}: Readonly<{
  dataAssetId: string
  metricRunAggregate?: MetricRunAggregate | null
  loading?: boolean
}>) {
  const isUserEditorOrHigher = useRequireRole("EDITOR")
  const should_show_loading = loading || !metricRunAggregate

  const hasFullCdmBeenRun = checkIfFullCdmHasBeenRun(metricRunAggregate)
  const cdmButtonLabel = hasFullCdmBeenRun ? "Refresh" : "Profile data"
  const cdmButtonLoadingLabel = hasFullCdmBeenRun ? "Refreshing..." : "Profiling data..."

  // rowCount is -1 if the full CDM has not been run, or the CDM job is incomplete.
  // We want to display a dash in this case, which is handled by the formatNumber function below.
  const rowCount = (metricRunAggregate?.rowCount ?? -1) > 0 ? metricRunAggregate?.rowCount : undefined
  const lastFetchedTooltipText = metricRunAggregate?.lastFetched
    ? formatLocalDateAsData(metricRunAggregate.lastFetched)
    : ""
  return (
    <Flex justify="space-between" style={{ padding: "20px" }}>
      <Flex gap="4px" align="center">
        <BodyStrong>Row count: </BodyStrong>
        {should_show_loading ? (
          <Skeleton loading paragraph={false} active round style={{ minWidth: 25 }} />
        ) : (
          formatNumber(rowCount)
        )}
      </Flex>
      <Flex gap="middle" align="center">
        <Flex gap="4px" align="center">
          <DescriptionNormal>Last fetched:</DescriptionNormal>
          {should_show_loading ? (
            <Skeleton loading paragraph={false} active round style={{ minWidth: 25 }} />
          ) : (
            <ConditionalTooltip tooltipText={lastFetchedTooltipText} showTooltip={Boolean(lastFetchedTooltipText)}>
              <DescriptionNormal>
                {!should_show_loading &&
                  formatLocalCalendarDateWithTime(
                    typeof metricRunAggregate.lastFetched === "string" ? metricRunAggregate.lastFetched : "",
                  )}
              </DescriptionNormal>
            </ConditionalTooltip>
          )}
        </Flex>
        <GenerateColumnDescriptiveMetricsButton
          dataAssetId={dataAssetId}
          label={cdmButtonLabel}
          loadingLabel={cdmButtonLoadingLabel}
          disabled={!isUserEditorOrHigher}
        />
      </Flex>
    </Flex>
  )
}
